import { Component, EventEmitter, Input, OnInit, Output, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgentPrompt } from '../../agent-admin/prompts/agent-prompt.models';
import { AgentPromptService } from '../../agent-admin/prompts/agent-prompt.service';

/**
 * Checkbox multi-select of the selected product's agent PROMPTS — used by the
 * category form to assign a deck category to one or more agents.
 *
 * Loads the prompt list itself (the parent page has already pointed
 * {@link AgentPromptService} at the product). Inactive prompts stay listed so
 * an existing assignment can be seen and removed, but are labeled as inactive.
 * An empty selection is a real state: the category is assigned to NO agent.
 */
@Component({
  selector: 'app-agent-multi-select',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <p *ngIf="loading()" class="px-3 py-3 text-xs text-slate-400" id="agent_multi_select_loading">
        Loading agents…
      </p>

      <p *ngIf="!loading() && !prompts().length" class="px-3 py-3 text-xs text-slate-400" id="agent_multi_select_empty">
        No agent prompts exist for this product yet — create one in the Agent → Prompts tab.
      </p>

      <label
        *ngFor="let prompt of prompts(); trackBy: trackByPrompt"
        class="flex items-center gap-3 px-3 py-2 border-b border-slate-50 dark:border-slate-700/30 last:border-b-0
               cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors"
      >
        <input
          type="checkbox"
          [checked]="isSelected(prompt.id)"
          (change)="toggle(prompt.id)"
          class="w-4 h-4 rounded accent-purple-600"
          [attr.id]="'agent_multi_select_' + prompt.id"
        />
        <span class="text-xs text-slate-700 dark:text-slate-200 flex-1 truncate">{{ prompt.name }}</span>
        <span
          *ngIf="!prompt.isActive"
          class="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-slate-400"
        >
          inactive
        </span>
      </label>
    </div>
  `,
})
export class AgentMultiSelectComponent implements OnInit {
  /** Currently selected prompt ids. */
  @Input() selected: number[] = [];
  @Output() selectedChange = new EventEmitter<number[]>();

  private promptService = inject(AgentPromptService);

  prompts = signal<AgentPrompt[]>([]);
  loading = signal(false);

  ngOnInit(): void {
    this.loading.set(true);
    this.promptService.listPrompts(true).subscribe({
      next: (res) => {
        this.prompts.set(res.prompts ?? []);
        this.loading.set(false);
      },
      error: () => {
        // The list degrades to empty — the category can still be saved without agents.
        this.prompts.set([]);
        this.loading.set(false);
      },
    });
  }

  isSelected(promptId: number): boolean {
    return this.selected.includes(promptId);
  }

  toggle(promptId: number): void {
    const next = this.isSelected(promptId)
      ? this.selected.filter((id) => id !== promptId)
      : [...this.selected, promptId];
    this.selectedChange.emit(next);
  }

  trackByPrompt(_: number, prompt: AgentPrompt): number {
    return prompt.id;
  }
}
